import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AppState, Athlete, Trade, Position } from '@/types';
import { priceAt, costToBuy, payoutToSell, FEE, type Curve } from '@/utils/pricing';
import { generateSeedAthletes, generateSeedTrades, generateSeedWallet, generateSeedProfile } from '@/utils/seedData';

const STORAGE_KEY = 'podiumx-state';

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      athletes: [],
      wallet: { usdc: 0, positions: {} },
      trades: [],
      userProfile: generateSeedProfile(),
      userAthleteId: undefined,
      initialized: false,

      initializeStore: () => {
        const state = get();
        if (!state.initialized) {
          const athletes = generateSeedAthletes();
          const trades = generateSeedTrades(athletes);
          const wallet = generateSeedWallet(athletes);
          
          set({
            athletes,
            trades,
            wallet,
            initialized: true,
          });
        }
      },

      buyTokens: (athleteId: string, quantity: number) => {
        const state = get();
        const athlete = state.athletes.find((a) => a.id === athleteId);
        if (!athlete) return;

        const curve: Curve = { a: 0.0002, b: 0.02, c: 1 };
        const grossCost = costToBuy(athlete.supply, quantity, curve);
        const fee = grossCost * FEE;
        const total = grossCost + fee;
        const newSupply = athlete.supply + quantity;
        const newPrice = priceAt(newSupply, curve);
        const avgPrice = grossCost / quantity;
        const newReserve = athlete.reserve + grossCost;
        
        // Check if user has enough USDC
        if (state.wallet.usdc < total) {
          throw new Error('Insufficient USDC balance');
        }

        // Update athlete
        const updatedAthletes = state.athletes.map((a) =>
          a.id === athleteId
            ? {
                ...a,
                supply: newSupply,
                reserve: newReserve,
                price: newPrice,
                marketCap: newPrice * newSupply,
                athleteRevenue: a.athleteRevenue + fee * 0.5,
                volume24h: a.volume24h + total,
              }
            : a
        );

        // Update wallet
        const currentPosition = state.wallet.positions[athleteId];
        const newPosition: Position = currentPosition
          ? {
              ...currentPosition,
              quantity: currentPosition.quantity + quantity,
              avgCost:
                (currentPosition.avgCost * currentPosition.quantity + grossCost) /
                (currentPosition.quantity + quantity),
              currentPrice: newPrice,
              pnl: 0,
              pnlPercent: 0,
            }
          : {
              athleteId,
              athleteName: athlete.name,
              quantity,
              avgCost: avgPrice,
              currentPrice: newPrice,
              pnl: 0,
              pnlPercent: 0,
            };

        // Recalculate PnL
        newPosition.pnl = (newPosition.currentPrice - newPosition.avgCost) * newPosition.quantity;
        newPosition.pnlPercent = ((newPosition.currentPrice - newPosition.avgCost) / newPosition.avgCost) * 100;

        const updatedWallet = {
          usdc: state.wallet.usdc - total,
          positions: {
            ...state.wallet.positions,
            [athleteId]: newPosition,
          },
        };

        // Add trade
        const trade: Trade = {
          id: `${Date.now()}-${athleteId}`,
          athleteId,
          athleteName: athlete.name,
          type: 'buy',
          quantity,
          price: avgPrice,
          total: total,
          fee: fee,
          timestamp: Date.now(),
        };

        set({
          athletes: updatedAthletes,
          wallet: updatedWallet,
          trades: [trade, ...state.trades],
        });
      },

      sellTokens: (athleteId: string, quantity: number) => {
        const state = get();
        const athlete = state.athletes.find((a) => a.id === athleteId);
        const position = state.wallet.positions[athleteId];

        if (!athlete || !position) return;
        if (position.quantity < quantity) {
          throw new Error('Insufficient token balance');
        }

        const curve: Curve = { a: 0.0002, b: 0.02, c: 1 };
        const grossPayout = payoutToSell(athlete.supply, quantity, curve);
        const fee = grossPayout * FEE;
        const total = grossPayout - fee;
        const newSupply = Math.max(0, athlete.supply - quantity);
        const newPrice = priceAt(newSupply, curve);
        const avgPrice = grossPayout / quantity;
        const newReserve = Math.max(0, athlete.reserve - grossPayout);

        // Update athlete
        const updatedAthletes = state.athletes.map((a) =>
          a.id === athleteId
            ? {
                ...a,
                supply: newSupply,
                reserve: newReserve,
                price: newPrice,
                marketCap: newPrice * newSupply,
                athleteRevenue: a.athleteRevenue + fee * 0.5,
                volume24h: a.volume24h + grossPayout,
              }
            : a
        );

        // Update wallet
        const newQuantity = position.quantity - quantity;
        const updatedPositions = { ...state.wallet.positions };

        if (newQuantity === 0) {
          delete updatedPositions[athleteId];
        } else {
          const newPosition = {
            ...position,
            quantity: newQuantity,
            currentPrice: newPrice,
            pnl: (newPrice - position.avgCost) * newQuantity,
            pnlPercent: ((newPrice - position.avgCost) / position.avgCost) * 100,
          };
          updatedPositions[athleteId] = newPosition;
        }

        const updatedWallet = {
          usdc: state.wallet.usdc + total,
          positions: updatedPositions,
        };

        // Calculate realized PnL for this trade
        const realizedPnL = (avgPrice - position.avgCost) * quantity;

        // Add trade
        const trade: Trade = {
          id: `${Date.now()}-${athleteId}`,
          athleteId,
          athleteName: athlete.name,
          type: 'sell',
          quantity,
          price: avgPrice,
          total: total,
          fee: fee,
          timestamp: Date.now(),
          userPnL: realizedPnL,
        };

        set({
          athletes: updatedAthletes,
          wallet: updatedWallet,
          trades: [trade, ...state.trades],
        });
      },

      createUserAthlete: (initialSupply: number) => {
        const state = get();
        const athleteId = `user-athlete-${Date.now()}`;
        const slug = state.userProfile.displayName.toLowerCase().replace(/\s+/g, '-');
        
        // Calculate initial reserve (0.1 USDC per token)
        const initialPrice = 0.1;
        const initialReserve = initialPrice * initialSupply;

        const newAthlete: Athlete = {
          id: athleteId,
          slug,
          name: state.userProfile.displayName,
          sport: state.userProfile.sport,
          avatar: state.userProfile.avatar || '',
          bio: state.userProfile.bio,
          location: state.userProfile.location,
          posts: [],
          socials: state.userProfile.socials,
          supply: initialSupply,
          reserve: initialReserve,
          price: initialPrice,
          marketCap: initialPrice * initialSupply,
          athleteRevenue: 0,
          change24h: 0,
          volume24h: 0,
          workouts: state.userProfile.workouts,
        };

        // Give user 1 token
        const position: Position = {
          athleteId,
          athleteName: newAthlete.name,
          quantity: 1,
          avgCost: initialPrice,
          currentPrice: initialPrice,
          pnl: 0,
          pnlPercent: 0,
        };

        set({
          athletes: [...state.athletes, newAthlete],
          userAthleteId: athleteId,
          wallet: {
            ...state.wallet,
            positions: {
              ...state.wallet.positions,
              [athleteId]: position,
            },
          },
        });
      },

      addWorkout: (workout) => {
        const state = get();
        const newWorkout = {
          ...workout,
          id: `${Date.now()}`,
          visibility: workout.visibility ?? 'public',
          minTokensRequired: workout.minTokensRequired ?? 0,
        };

        set({
          userProfile: {
            ...state.userProfile,
            workouts: [newWorkout, ...state.userProfile.workouts],
          },
        });
      },

      updateWorkout: (id, updates) => {
        const state = get();
        set({
          userProfile: {
            ...state.userProfile,
            workouts: state.userProfile.workouts.map((w) =>
              w.id === id ? { ...w, ...updates } : w
            ),
          },
        });
      },

      deleteWorkout: (id) => {
        const state = get();
        set({
          userProfile: {
            ...state.userProfile,
            workouts: state.userProfile.workouts.filter((w) => w.id !== id),
          },
        });
      },

      updateProfile: (updates) => {
        const state = get();
        set({
          userProfile: {
            ...state.userProfile,
            ...updates,
          },
        });
      },

      faucet: (amount) => {
        const state = get();
        set({
          wallet: {
            ...state.wallet,
            usdc: state.wallet.usdc + amount,
          },
        });
      },

      resetDemo: () => {
        const athletes = generateSeedAthletes();
        const trades = generateSeedTrades(athletes);
        const wallet = generateSeedWallet(athletes);
        const userProfile = generateSeedProfile();

        set({
          athletes,
          trades,
          wallet,
          userProfile,
          initialized: true,
        });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    }
  )
);
